/**
 * Canvas Context
 *
 * Manages canvas and surface state for the current conversation.
 * Subscribes to real-time WS events and fetches initial state from REST.
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { spaceApi } from '../services/api';
import { usePlatformEvents } from './PlatformEventContext';
import { useToast } from '../components/ui/Toast';
import type { PlatformEvent } from '../types/events';
import type {
  Canvas,
  Surface,
  CanvasLayout,
  CanvasCreatedEventData,
  SurfaceUpdateEventData,
  SurfaceDeletedEventData,
  SurfaceMovedEventData,
  CanvasEntityUpdatedEventData,
  SurfaceEntityUpdatedEventData,
} from '../types/canvases';
import { compareCanvases, compareSurfaces } from '../types/canvases';
import { cleanDisplayText } from '@/utils/display-text';

// ─── State ───────────────────────────────────────────────────────────

interface ScrollTarget {
  canvasId: string;
  surfaceId: string;
}

export interface CanvasState {
  canvases: Map<string, Canvas>;
  surfaces: Map<string, Surface>; // key: "cvsId:srfId"
  activeCanvasId: string | null;
  loaded: boolean;
  scrollToSurface: ScrollTarget | null;
  /** canvasId → surfaceId of last update (for non-active canvases when autoScroll is off) */
  pendingUpdates: Map<string, string>;
  /** Canvases whose surfaces have been fetched at least once this session. */
  loadedCanvases: Set<string>;
  /** Canvases whose surface fetch is currently in flight. */
  loadingCanvases: Set<string>;
}

export const initialState: CanvasState = {
  canvases: new Map(),
  surfaces: new Map(),
  activeCanvasId: null,
  loaded: false,
  scrollToSurface: null,
  pendingUpdates: new Map(),
  loadedCanvases: new Set(),
  loadingCanvases: new Set(),
};

// ─── Actions ─────────────────────────────────────────────────────────

type CanvasAction =
  | { type: 'canvas_created'; data: CanvasCreatedEventData }
  | { type: 'surface_updated'; data: SurfaceUpdateEventData; timestamp?: string; scroll?: boolean; pending?: boolean }
  | { type: 'set_active_canvas'; canvasId: string }
  | { type: 'surface_removed'; canvasId: string; surfaceId: string }
  | {
      type: 'surface_moved';
      fromCanvasId: string;
      toCanvasId: string;
      surfaceId: string;
      version: number;
      toConversationId?: string;
      targetCanvas?: { title: string; layout: CanvasLayout };
    }
  | { type: 'canvas_removed'; canvasId: string }
  | { type: 'canvas_metadata_updated'; data: CanvasEntityUpdatedEventData }
  | { type: 'surface_metadata_updated'; data: SurfaceEntityUpdatedEventData }
  | { type: 'canvases_reordered'; orderedCanvasIds: string[] }
  | { type: 'surfaces_reordered'; canvasId: string; orderedSurfaceIds: string[] }
  | { type: 'bulk_load'; canvases: Canvas[]; surfaces: Surface[]; loadedCanvasIds?: string[] }
  | { type: 'mark_canvas_loading'; canvasId: string }
  | { type: 'mark_canvas_loaded'; canvasId: string }
  | { type: 'remove_surface_node'; canvasId: string; surfaceId: string; nodeId: string }
  | { type: 'surface_uistate_replaced'; canvasId: string; surfaceId: string; uiState: Record<string, unknown> }
  | { type: 'scroll_to_surface'; canvasId: string; surfaceId: string }
  | { type: 'clear_scroll_target' }
  | { type: 'clear' };

function surfaceKey(canvasId: string, surfaceId: string): string {
  return `${canvasId}:${surfaceId}`;
}

export function canvasReducer(state: CanvasState, action: CanvasAction): CanvasState {
  switch (action.type) {
    case 'canvas_created': {
      const { canvasId, title, isDefault, layout, conversationId } = action.data;
      const existing = state.canvases.get(canvasId);
      if (existing) return state;

      const canvas: Canvas = {
        canvasId,
        conversationId: conversationId ?? '',
        title,
        isDefault,
        layout,
        createdAt: new Date().toISOString(),
      };

      const canvases = new Map(state.canvases);
      canvases.set(canvasId, canvas);

      // Auto-select default canvas, or first canvas if none selected
      const activeCanvasId =
        isDefault || !state.activeCanvasId
          ? canvasId
          : state.activeCanvasId;

      return { ...state, canvases, activeCanvasId };
    }

    case 'surface_updated': {
      const { canvasId, surfaceId, title, version, spec } = action.data;
      const key = surfaceKey(canvasId, surfaceId);
      const existing = state.surfaces.get(key);

      // Only accept if version is newer or equal (idempotent)
      if (existing && existing.version > version) return state;

      // An event may arrive without a spec (metadata-only payload, or a
      // wrapped API response leaking through a recovery fetch). Preserve
      // the existing spec; bail entirely when there's nothing to render.
      if (!spec) {
        if (!existing) return state;
        const surface: Surface = {
          ...existing,
          title,
          version,
          updatedAt: action.data.updatedAt || action.timestamp || new Date().toISOString(),
        };
        const surfaces = new Map(state.surfaces);
        surfaces.set(key, surface);
        return { ...state, surfaces };
      }

      // Merge incoming nodes with existing nodes so that nodes from
      // previous updates (e.g. completed DelegatedTaskCards) aren't lost
      // when the agent sends a partial update with only active tasks.
      // A WS `surface_update` event may carry a `spec` object whose `updates`
      // array is missing (the type claims it's always present, but runtime
      // payloads don't honor that). Normalize to an array so the merge loops
      // below never dereference undefined — a throwing reducer unmounts the
      // whole React tree and blanks the conversation.
      const incomingUpdates = Array.isArray(spec.updates) ? spec.updates : [];
      let mergedSpec = spec;
      if (existing?.spec?.updates) {
        const existingNodeMap = new Map(
          existing.spec.updates.map((n) => [n.id, n]),
        );

        // Auto-inject previousContent for MarkdownEditor nodes so the UI
        // can show diffs against the prior surface version.
        for (const node of incomingUpdates) {
          if (node.component.type === 'MarkdownEditor') {
            const prevNode = existingNodeMap.get(node.id);
            if (prevNode?.component.type === 'MarkdownEditor') {
              const prevContent = prevNode.component.props?.content as string | undefined;
              const newContent = node.component.props?.content as string | undefined;
              if (
                prevContent &&
                newContent &&
                prevContent !== newContent &&
                !node.component.props?.previousContent
              ) {
                node.component.props = {
                  ...node.component.props,
                  previousContent: prevContent,
                };
              }
            }
          }
        }

        // Build merged node map: start with existing, overlay incoming
        const mergedNodeMap = new Map(existingNodeMap);
        const incomingIds = new Set<string>();
        for (const node of incomingUpdates) {
          mergedNodeMap.set(node.id, node);
          incomingIds.add(node.id);
        }

        // For container nodes (Grid, Column, Row) in the incoming update,
        // merge children: incoming children first, then existing children
        // that aren't already referenced in the incoming update.
        for (const node of incomingUpdates) {
          const newChildren = node.component.children;
          if (!newChildren) continue;
          const prevNode = existingNodeMap.get(node.id);
          const prevChildren = prevNode?.component.children;
          if (!prevChildren || prevChildren.length === 0) continue;

          const newChildSet = new Set(newChildren);
          const retained = prevChildren.filter((id) => !newChildSet.has(id));
          if (retained.length > 0) {
            node.component.children = [...newChildren, ...retained];
            // Ensure retained child nodes are in the merged map
            for (const id of retained) {
              if (!mergedNodeMap.has(id)) {
                const old = existingNodeMap.get(id);
                if (old) mergedNodeMap.set(id, old);
              }
            }
          }
        }

        mergedSpec = { ...spec, updates: [...mergedNodeMap.values()] };
      }

      const { runId } = action.data;
      const now = action.data.updatedAt || action.timestamp || new Date().toISOString();
      // Apply uiState from the event when present; otherwise preserve what
      // we already have. The dispatcher strips uiState from the action data
      // when the surface has unflushed local edits (race A — see Provider).
      const incomingUiState = action.data.uiState;
      const finalUiState = incomingUiState !== undefined ? incomingUiState : existing?.uiState;
      // `surfaceName` is needed to dispatch AUI action clicks (see
      // useDispatchAction). The surface_update event carries it; fall back to the
      // existing surface's name when a metadata-only payload omits it.
      const surfaceName = action.data.surfaceName ?? existing?.surfaceName;
      const surface: Surface = {
        surfaceId, canvasId, runId: runId || undefined, title, version,
        ...(surfaceName ? { surfaceName } : {}),
        // Guarantee spec.updates is always an array so downstream consumers
        // (compareSurfaces, AuiDelegatedTaskCard) can iterate it unguarded.
        spec: { ...mergedSpec, updates: Array.isArray(mergedSpec?.updates) ? mergedSpec.updates : [] },
        // Preserve `order` set by the API (used by compareSurfaces). The
        // surface_update WebSocket event doesn't carry order, so fall back
        // to the existing surface's order when merging.
        ...(existing?.order !== undefined ? { order: existing.order } : {}),
        createdAt: existing?.createdAt || action.data.createdAt || now,
        updatedAt: now,
        ...(finalUiState !== undefined ? { uiState: finalUiState } : {}),
      };
      const surfaces = new Map(state.surfaces);
      surfaces.set(key, surface);

      // Auto-create canvas if it doesn't exist yet (backend may not emit
      // canvas_created before surface_update). Only mark the auto-created
      // canvas as default when no other default exists — otherwise a
      // delegation-spawned canvas would collide with the conversation's
      // existing default and get filtered out of the tab strip.
      let { canvases, activeCanvasId } = state;
      if (!canvases.has(canvasId)) {
        const hasExistingDefault = [...canvases.values()].some((w) => w.isDefault);
        canvases = new Map(canvases);
        canvases.set(canvasId, {
          canvasId,
          conversationId: '',
          title: title || 'Canvas',
          isDefault: !hasExistingDefault,
          layout: 'stack',
          createdAt: new Date().toISOString(),
        });
        if (!activeCanvasId) {
          activeCanvasId = canvasId;
        }
      }

      let { scrollToSurface, pendingUpdates } = state;
      if (action.scroll) {
        // Auto-scroll mode (e.g. Conversations page): switch and scroll
        scrollToSurface = { canvasId, surfaceId };
      } else if (action.pending && canvasId !== activeCanvasId) {
        // Non-auto-scroll mode (e.g. Operator page): track as pending update
        pendingUpdates = new Map(pendingUpdates);
        pendingUpdates.set(canvasId, surfaceId);
      }

      return { ...state, surfaces, canvases, activeCanvasId, scrollToSurface, pendingUpdates };
    }

    case 'surface_removed': {
      const key = surfaceKey(action.canvasId, action.surfaceId);
      if (!state.surfaces.has(key)) return state;
      const surfaces = new Map(state.surfaces);
      surfaces.delete(key);
      return { ...state, surfaces };
    }

    case 'surface_moved': {
      const fromKey = surfaceKey(action.fromCanvasId, action.surfaceId);
      const surface = state.surfaces.get(fromKey);
      if (!surface) return state;

      const surfaces = new Map(state.surfaces);
      surfaces.delete(fromKey);

      // Cross-conversation move: surface left this provider's conversation.
      // Drop it from the source and don't insert at the target — that surface
      // belongs to a different conversation now.
      const ownConversationId = [...state.canvases.values()][0]?.conversationId;
      if (
        action.toConversationId &&
        ownConversationId &&
        action.toConversationId !== ownConversationId
      ) {
        return { ...state, surfaces };
      }

      let canvases = state.canvases;
      if (!canvases.has(action.toCanvasId) && action.targetCanvas) {
        canvases = new Map(canvases);
        canvases.set(action.toCanvasId, {
          canvasId: action.toCanvasId,
          conversationId: ownConversationId ?? '',
          title: action.targetCanvas.title,
          isDefault: false,
          layout: action.targetCanvas.layout,
          createdAt: new Date().toISOString(),
        });
      }

      const toKey = surfaceKey(action.toCanvasId, action.surfaceId);
      surfaces.set(toKey, {
        ...surface,
        canvasId: action.toCanvasId,
        version: action.version,
        updatedAt: new Date().toISOString(),
      });

      return { ...state, canvases, surfaces };
    }

    case 'canvas_removed': {
      if (!state.canvases.has(action.canvasId)) return state;
      const canvases = new Map(state.canvases);
      canvases.delete(action.canvasId);

      // Remove all surfaces belonging to this canvas
      const surfaces = new Map(state.surfaces);
      for (const [key, srf] of surfaces) {
        if (srf.canvasId === action.canvasId) surfaces.delete(key);
      }

      // If the removed canvas was active, switch to default or first remaining
      let { activeCanvasId } = state;
      if (activeCanvasId === action.canvasId) {
        const defaultCv = [...canvases.values()].find((w) => w.isDefault);
        activeCanvasId = defaultCv?.canvasId ?? [...canvases.keys()][0] ?? null;
      }

      return { ...state, canvases, surfaces, activeCanvasId };
    }

    case 'canvas_metadata_updated': {
      const { canvasId, title, layout, order } = action.data;
      const existing = state.canvases.get(canvasId);
      if (!existing) return state;
      const next: Canvas = {
        ...existing,
        ...(title !== undefined ? { title } : {}),
        ...(layout !== undefined ? { layout } : {}),
        ...(order !== undefined ? { order } : {}),
      };
      const canvases = new Map(state.canvases);
      canvases.set(canvasId, next);
      return { ...state, canvases };
    }

    case 'surface_metadata_updated': {
      const { surfaceId, canvasId, title, order } = action.data;
      const key = surfaceKey(canvasId, surfaceId);
      const existing = state.surfaces.get(key);
      if (!existing) return state;
      const next: Surface = {
        ...existing,
        ...(title !== undefined ? { title } : {}),
        ...(order !== undefined ? { order } : {}),
      };
      const surfaces = new Map(state.surfaces);
      surfaces.set(key, next);
      return { ...state, surfaces };
    }

    case 'canvases_reordered': {
      const canvases = new Map(state.canvases);
      action.orderedCanvasIds.forEach((id, idx) => {
        const cv = canvases.get(id);
        if (cv) canvases.set(id, { ...cv, order: idx + 1 });
      });
      return { ...state, canvases };
    }

    case 'surfaces_reordered': {
      const surfaces = new Map(state.surfaces);
      action.orderedSurfaceIds.forEach((id, idx) => {
        const key = surfaceKey(action.canvasId, id);
        const srf = surfaces.get(key);
        if (srf) surfaces.set(key, { ...srf, order: idx + 1 });
      });
      return { ...state, surfaces };
    }

    case 'set_active_canvas': {
      const pendingUpdates = new Map(state.pendingUpdates);
      pendingUpdates.delete(action.canvasId);
      return { ...state, activeCanvasId: action.canvasId, pendingUpdates };
    }

    case 'bulk_load': {
      const canvases = new Map<string, Canvas>();
      const surfaces = new Map<string, Surface>();

      for (const cv of action.canvases) {
        canvases.set(cv.canvasId, cv);
      }
      for (const srf of action.surfaces) {
        surfaces.set(surfaceKey(srf.canvasId, srf.surfaceId), srf);
      }

      // Select default canvas, or first
      const defaultCv = action.canvases.find((w) => w.isDefault);
      const activeCanvasId =
        defaultCv?.canvasId ?? action.canvases[0]?.canvasId ?? null;

      return {
        canvases,
        surfaces,
        activeCanvasId,
        loaded: true,
        scrollToSurface: null,
        pendingUpdates: new Map(),
        loadedCanvases: new Set(action.loadedCanvasIds ?? []),
        loadingCanvases: new Set(),
      };
    }

    case 'mark_canvas_loading': {
      if (
        state.loadingCanvases.has(action.canvasId) ||
        state.loadedCanvases.has(action.canvasId)
      ) {
        return state;
      }
      const loadingCanvases = new Set(state.loadingCanvases);
      loadingCanvases.add(action.canvasId);
      return { ...state, loadingCanvases };
    }

    case 'mark_canvas_loaded': {
      const wasLoading = state.loadingCanvases.has(action.canvasId);
      const wasLoaded = state.loadedCanvases.has(action.canvasId);
      if (wasLoaded && !wasLoading) return state;
      const loadedCanvases = wasLoaded
        ? state.loadedCanvases
        : new Set(state.loadedCanvases).add(action.canvasId);
      const loadingCanvases = wasLoading
        ? new Set([...state.loadingCanvases].filter((id) => id !== action.canvasId))
        : state.loadingCanvases;
      return { ...state, loadedCanvases, loadingCanvases };
    }

    case 'surface_uistate_replaced': {
      const key = surfaceKey(action.canvasId, action.surfaceId);
      const surface = state.surfaces.get(key);
      if (!surface) return state;
      const surfaces = new Map(state.surfaces);
      surfaces.set(key, { ...surface, uiState: action.uiState });
      return { ...state, surfaces };
    }

    case 'remove_surface_node': {
      const key = surfaceKey(action.canvasId, action.surfaceId);
      const surface = state.surfaces.get(key);
      if (!surface?.spec?.updates) return state;
      const updatedNodes = surface.spec.updates
        .filter((n) => n.id !== action.nodeId)
        .map((n) => {
          // Remove from parent children arrays
          if (n.component.children?.includes(action.nodeId)) {
            return { ...n, component: { ...n.component, children: n.component.children.filter((id) => id !== action.nodeId) } };
          }
          return n;
        });
      const surfaces = new Map(state.surfaces);
      surfaces.set(key, { ...surface, spec: { updates: updatedNodes } });
      return { ...state, surfaces };
    }

    case 'scroll_to_surface':
      return { ...state, scrollToSurface: { canvasId: action.canvasId, surfaceId: action.surfaceId } };

    case 'clear_scroll_target':
      return state.scrollToSurface ? { ...state, scrollToSurface: null } : state;

    case 'clear':
      return {
        ...initialState,
        pendingUpdates: new Map(),
        loadedCanvases: new Set(),
        loadingCanvases: new Set(),
        loaded: true,
      };

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────

interface CanvasContextValue {
  state: CanvasState;
  dispatch: (action: CanvasAction) => void;
  conversationId: string | undefined;
  /** Owning space — needed by the consumer hooks (`useCanvases`) to call
   *  the space-scoped canvas/surface endpoints. */
  spaceId: string | undefined;
  autoScrollOnUpdate: boolean;
  /**
   * Persist a single component's slice of uiState. Wraps the slice with a
   * `kind` discriminator so reads from a different component type return
   * defaults instead of mismatched data. Optimistically updates context
   * state and debounces (500 ms) a PATCH to the backend.
   */
  setSurfaceNodeUiState: (
    surfaceId: string,
    nodeId: string,
    kind: string,
    data: unknown,
  ) => void;
  /**
   * Replace the entire uiState blob for a surface. Used for stale-slice
   * GC; not for normal component edits.
   */
  setSurfaceUiStateRaw: (surfaceId: string, uiState: Record<string, unknown>) => void;
  /** Synchronous flush of any pending uiState debounce for the given surface. */
  flushSurfaceUiState: (surfaceId?: string) => void;
}

export const CanvasContext = createContext<CanvasContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────

interface CanvasHint {
  canvasId: string;
  title: string;
  isDefault: boolean;
  layout?: string;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CanvasProviderProps {
  conversationId: string | undefined;
  /** Owning space for the conversation. Backend rule: canvas/surface
   *  endpoints are space-scoped, so the provider can't make any of its
   *  reads/writes without it. Provider sits idle until this resolves.
   *  Dev preview pages omit this — they only render seeded data. */
  spaceId?: string;
  canvasHints?: CanvasHint[];
  initialCanvas?: { canvas: Canvas; surfaces: Surface[] } | null;
  /** When false, incoming surface updates won't auto-switch tabs/scroll. Default: true. */
  autoScrollOnUpdate?: boolean;
  children: ReactNode;
}

export function CanvasProvider({ conversationId, spaceId, canvasHints, initialCanvas, autoScrollOnUpdate = true, children }: CanvasProviderProps) {
  const [state, dispatch] = useReducer(canvasReducer, initialState);
  const { subscribe } = usePlatformEvents();
  const prevConversationId = useRef<string | undefined>(undefined);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Stable space-scoped api handle. All canvas/surface reads + writes go
  // through `/spaces/{spaceId}/...`; if spaceId isn't ready yet (caller is
  // still resolving the conversation's space), every method becomes a noop
  // and the provider simply waits.
  const spaceIdRef = useRef(spaceId);
  spaceIdRef.current = spaceId;
  const cvApi = (spaceId ? spaceApi(spaceId).canvases : null);

  // ─── uiState persistence (race A + race B handling) ─────────────────
  // Refs only — these never need to trigger a render.
  // Per-surface generation counter, incremented on every local edit.
  const pendingUiStateGen = useRef<Map<string, number>>(new Map());
  // 500 ms debounce timers per surface.
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // 5 s safety timeout per surface — clears the dirty marker if a PATCH hangs.
  const dirtyTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Latest uiState we plan to PATCH for each dirty surface.
  const latestUiStateBySurface = useRef<Map<string, Record<string, unknown>>>(new Map());
  // Pre-edit uiState (used to revert if PATCH fails).
  const previousUiStateBySurface = useRef<Map<string, Record<string, unknown> | undefined>>(new Map());
  // Spec version observed when each in-flight PATCH was queued. Compared
  // against the response's echoed version to detect race B.
  const queuedVersionBySurface = useRef<Map<string, number>>(new Map());

  const toastRef = useRef<ReturnType<typeof useToast> | null>(null);

  // Strip uiState from incoming surface_updated payloads when the surface has
  // unflushed local edits. Otherwise the agent's update_surface event would
  // echo the *server-current* (pre-edit) uiState and clobber our optimistic
  // changes before our PATCH lands.
  const dispatchSurfaceUpdated = useCallback((opts: {
    data: SurfaceUpdateEventData;
    timestamp?: string;
    scroll?: boolean;
    pending?: boolean;
  }) => {
    const { data } = opts;
    const isDirty = pendingUiStateGen.current.has(data.surfaceId);
    const cleaned = isDirty && data.uiState !== undefined
      ? { ...data, uiState: undefined as Record<string, unknown> | undefined }
      : data;
    dispatch({
      type: 'surface_updated',
      data: cleaned,
      timestamp: opts.timestamp,
      scroll: opts.scroll,
      pending: opts.pending,
    });
  }, []);

  const clearDirty = useCallback((surfaceId: string) => {
    pendingUiStateGen.current.delete(surfaceId);
    latestUiStateBySurface.current.delete(surfaceId);
    previousUiStateBySurface.current.delete(surfaceId);
    queuedVersionBySurface.current.delete(surfaceId);
    const t = dirtyTimeouts.current.get(surfaceId);
    if (t) {
      clearTimeout(t);
      dirtyTimeouts.current.delete(surfaceId);
    }
  }, []);

  const handleRaceB = useCallback((surfaceId: string, canvasId: string, responseVersion: number) => {
    const queued = queuedVersionBySurface.current.get(surfaceId) ?? 0;
    if (responseVersion <= queued) return; // no race
    // Agent re-rendered while our PATCH was in flight. Wait briefly for the
    // surface_update WS event to land before refetching.
    setTimeout(() => {
      const currentVersion = (() => {
        for (const s of stateRef.current.surfaces.values()) {
          if (s.surfaceId === surfaceId) return s.version;
        }
        return 0;
      })();
      if (currentVersion >= responseVersion) return;
      if (!conversationIdRef.current) return;
      if (!cvApi) return;
      cvApi
        .getSurface(canvasId, surfaceId)
        .then((s) => {
          dispatchSurfaceUpdated({
            data: {
              ...s,
              runId: '',
              agentId: '',
              iteration: 0,
              canvasId: s.canvasId,
            },
          });
        })
        .catch(() => {});
    }, 500);
  }, [dispatchSurfaceUpdated]);

  // Find a surface across all canvases by id (uiState is keyed only by
  // surfaceId; we look up canvas from the state for the API call).
  const findSurfaceById = useCallback((surfaceId: string): Surface | undefined => {
    for (const s of stateRef.current.surfaces.values()) {
      if (s.surfaceId === surfaceId) return s;
    }
    return undefined;
  }, []);

  // Fire the PATCH and handle the response. Captures the generation at queue
  // time so a slow PATCH that resolves after a newer edit doesn't prematurely
  // clear the dirty marker.
  const flushOne = useCallback((surfaceId: string) => {
    debounceTimers.current.delete(surfaceId);
    const surface = findSurfaceById(surfaceId);
    if (!surface) return;
    const uiState = latestUiStateBySurface.current.get(surfaceId);
    if (uiState === undefined) return;
    const queuedGen = pendingUiStateGen.current.get(surfaceId) ?? 0;
    const canvasId = surface.canvasId;
    const previous = previousUiStateBySurface.current.get(surfaceId);
    // Snapshot the spec version we're queuing against so we can detect
    // race B (agent re-renders while our PATCH is in flight).
    queuedVersionBySurface.current.set(surfaceId, surface.version);

    if (!cvApi) return;
    cvApi
      .updateSurfaceUiState(canvasId, surfaceId, uiState)
      .then((res) => {
        const currentGen = pendingUiStateGen.current.get(surfaceId) ?? 0;
        if (currentGen === queuedGen) {
          // No newer edits queued — safe to clear dirty.
          clearDirty(surfaceId);
        }
        handleRaceB(surfaceId, canvasId, res.version);
      })
      .catch((err: Error & { code?: string; status?: number }) => {
        // 404: surface gone — silently drop the dirty marker.
        const status = (err as { status?: number }).status;
        if (status === 404 || err.code === 'NOT_FOUND') {
          clearDirty(surfaceId);
          return;
        }
        // 413: payload too large — revert to previous. Don't retry.
        if (status === 413 || err.code === 'PAYLOAD_TOO_LARGE') {
          dispatch({
            type: 'surface_uistate_replaced',
            canvasId,
            surfaceId,
            uiState: previous ?? {},
          });
          clearDirty(surfaceId);
          toastRef.current?.error('View settings too large to save');
          return;
        }
        // Transient failure — revert and toast.
        dispatch({
          type: 'surface_uistate_replaced',
          canvasId,
          surfaceId,
          uiState: previous ?? {},
        });
        clearDirty(surfaceId);
        toastRef.current?.error('Could not save view');
      });
  }, [findSurfaceById, clearDirty, handleRaceB]);

  const flushSurfaceUiState = useCallback((surfaceId?: string) => {
    if (surfaceId) {
      const t = debounceTimers.current.get(surfaceId);
      if (t) {
        clearTimeout(t);
        flushOne(surfaceId);
      }
      return;
    }
    // Flush all
    for (const id of [...debounceTimers.current.keys()]) {
      const t = debounceTimers.current.get(id);
      if (t) clearTimeout(t);
      flushOne(id);
    }
  }, [flushOne]);

  const setSurfaceNodeUiState = useCallback(
    (surfaceId: string, nodeId: string, kind: string, data: unknown) => {
      const surface = findSurfaceById(surfaceId);
      if (!surface) return;
      const previous = surface.uiState ?? {};
      // Capture pre-edit value once per dirty cycle so revert restores the
      // server-known state, not an intermediate optimistic value.
      if (!previousUiStateBySurface.current.has(surfaceId)) {
        previousUiStateBySurface.current.set(surfaceId, previous);
      }
      const slice = { kind, v: 1, data };
      const next: Record<string, unknown> = { ...previous, [nodeId]: slice };

      // Optimistic dispatch.
      dispatch({
        type: 'surface_uistate_replaced',
        canvasId: surface.canvasId,
        surfaceId,
        uiState: next,
      });

      // Bookkeeping for the debounced PATCH.
      pendingUiStateGen.current.set(
        surfaceId,
        (pendingUiStateGen.current.get(surfaceId) ?? 0) + 1,
      );
      latestUiStateBySurface.current.set(surfaceId, next);

      const existingTimer = debounceTimers.current.get(surfaceId);
      if (existingTimer) clearTimeout(existingTimer);
      debounceTimers.current.set(
        surfaceId,
        setTimeout(() => flushOne(surfaceId), 500),
      );

      // 5 s safety: if dirty doesn't clear, force-clear so server updates
      // can flow again. Reset on each edit so the window is rolling.
      const existingSafety = dirtyTimeouts.current.get(surfaceId);
      if (existingSafety) clearTimeout(existingSafety);
      dirtyTimeouts.current.set(
        surfaceId,
        setTimeout(() => clearDirty(surfaceId), 5000),
      );
    },
    [findSurfaceById, flushOne, clearDirty],
  );

  const setSurfaceUiStateRaw = useCallback(
    (surfaceId: string, uiState: Record<string, unknown>) => {
      const surface = findSurfaceById(surfaceId);
      if (!surface) return;
      if (!previousUiStateBySurface.current.has(surfaceId)) {
        previousUiStateBySurface.current.set(surfaceId, surface.uiState ?? {});
      }
      dispatch({
        type: 'surface_uistate_replaced',
        canvasId: surface.canvasId,
        surfaceId,
        uiState,
      });
      pendingUiStateGen.current.set(
        surfaceId,
        (pendingUiStateGen.current.get(surfaceId) ?? 0) + 1,
      );
      latestUiStateBySurface.current.set(surfaceId, uiState);
      const existingTimer = debounceTimers.current.get(surfaceId);
      if (existingTimer) clearTimeout(existingTimer);
      debounceTimers.current.set(
        surfaceId,
        setTimeout(() => flushOne(surfaceId), 500),
      );
    },
    [findSurfaceById, flushOne],
  );

  // Flush via sendBeacon on page unload — fetch is unreliable during unload.
  useEffect(() => {
    const handler = () => {
      for (const id of [...latestUiStateBySurface.current.keys()]) {
        const surface = findSurfaceById(id);
        if (!surface) continue;
        const uiState = latestUiStateBySurface.current.get(id);
        if (uiState === undefined) continue;
        try {
          const body = new Blob([JSON.stringify({ uiState })], { type: 'application/json' });
          if (cvApi) {
            navigator.sendBeacon(cvApi.surfaceUiStateUrl(surface.canvasId, id), body);
          }
        } catch {
          /* best-effort only */
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [findSurfaceById]);

  // On unmount, flush any pending writes. The cleanup runs synchronously,
  // so we can fire the PATCH; React doesn't await it but the request is
  // already in flight.
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      for (const id of [...timers.keys()]) {
        const t = timers.get(id);
        if (t) clearTimeout(t);
        flushOne(id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load canvas state from hints + pre-fetched initial canvas
  useEffect(() => {
    if (!conversationId) return;
    if (conversationId === prevConversationId.current) return;
    prevConversationId.current = conversationId;

    const canvases: Canvas[] = (canvasHints ?? []).map((h) => ({
      canvasId: h.canvasId,
      conversationId: conversationId!,
      title: h.title,
      isDefault: h.isDefault,
      layout: (h.layout as Canvas['layout']) ?? 'stack',
      ...(h.order !== undefined ? { order: h.order } : {}),
      createdAt: h.createdAt ?? new Date().toISOString(),
    }));

    // Merge full metadata from initialCanvas if available
    if (initialCanvas?.canvas) {
      const idx = canvases.findIndex((w) => w.canvasId === initialCanvas.canvas.canvasId);
      if (idx !== -1) canvases[idx] = initialCanvas.canvas;
    }

    const surfaces = initialCanvas?.surfaces ?? [];
    const loadedCanvasIds = initialCanvas?.canvas
      ? [initialCanvas.canvas.canvasId]
      : [];

    if (canvases.length === 0 && surfaces.length === 0) {
      dispatch({ type: 'bulk_load', canvases: [], surfaces: [], loadedCanvasIds });
    } else {
      dispatch({ type: 'bulk_load', canvases, surfaces, loadedCanvasIds });
    }
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch surfaces for the active canvas if we haven't loaded them yet.
  // Only the default canvas is eagerly fetched in useConversationRestore;
  // other canvases are loaded the first time the user activates their tab.
  useEffect(() => {
    const cvId = state.activeCanvasId;
    if (!conversationId || !cvId) return;
    if (state.loadedCanvases.has(cvId) || state.loadingCanvases.has(cvId)) return;

    // Mark loading synchronously to prevent re-entry from rapid tab clicks.
    dispatch({ type: 'mark_canvas_loading', canvasId: cvId });

    if (!cvApi) {
      // No API client (e.g. dev preview mounts without a spaceId) — there is
      // nothing to fetch, so mark loaded or the canvas spins forever and any
      // surfaces seeded via bulk_load never render.
      dispatch({ type: 'mark_canvas_loaded', canvasId: cvId });
      return;
    }
    cvApi
      .get(conversationId, cvId)
      .then((result) => {
        if (result) {
          for (const s of result.surfaces) {
            dispatchSurfaceUpdated({
              data: { ...s, runId: '', agentId: '', iteration: 0 },
            });
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        // Mark loaded on success or failure — we don't auto-retry; user can
        // refresh the page if they hit a transient error.
        dispatch({ type: 'mark_canvas_loaded', canvasId: cvId });
      });
  }, [conversationId, state.activeCanvasId, state.loadedCanvases, state.loadingCanvases, dispatchSurfaceUpdated]);

  // Fetch a canvas's surfaces from the REST API and render them. Used to
  // recover surface CONTENT that has no live transport: the direct-run socket
  // carries `agent.run.*` run events but NOT surface lifecycle events
  // (`agent.run.surface_update` / `canvas_created`), and those aren't fanned out
  // to EventBridge for direct-run runs either. The platform DOES emit the
  // metadata-only `entity.{canvas,surface}.created` events to the fan-out, so we
  // subscribe to those (below) and fetch the spec here to render live — instead
  // of the surface only appearing on a full page reload.
  const loadCanvasContent = useCallback((canvasId: string) => {
    const cid = conversationIdRef.current;
    if (!cid || !cvApi) return;
    cvApi
      .get(cid, canvasId)
      .then((result) => {
        if (!result) return;
        for (const s of result.surfaces) {
          dispatchSurfaceUpdated({
            data: { ...s, runId: '', agentId: '', iteration: 0 },
            scroll: autoScrollOnUpdate,
            pending: !autoScrollOnUpdate,
          });
        }
      })
      .catch(() => {/* transient — next event or reload recovers */});
  }, [cvApi, dispatchSurfaceUpdated, autoScrollOnUpdate]);

  // Subscribe to real-time canvas events via WebSocket.
  // Gated on conversationId so we never ingest events for a different
  // conversation. Any events missed before conversationId is set are
  // recovered by the bulk_load from the REST API on conversation restore.
  useEffect(() => {
    if (!conversationIdRef.current) return;

    const unsubscribe = subscribe(
      [
        // These carry data.conversationId — scope server-side. (surface_moved
        // carries from/toConversationId instead, and the entity.* events have
        // their own shape, so both stay unscoped and rely on the handler gate.)
        { eventType: 'agent.run.canvas_created', conversationId },
        { eventType: 'agent.run.surface_update', conversationId },
        { eventType: 'agent.run.surface_deleted', conversationId },
        { eventType: 'agent.run.surface_moved' },
        { eventType: 'entity.canvas.created' },
        { eventType: 'entity.canvas.updated' },
        { eventType: 'entity.surface.created' },
        { eventType: 'entity.surface.updated' },
      ],
      (event: PlatformEvent) => {
        const data = event.data as Record<string, unknown>;
        const ownConversationId = conversationIdRef.current;
        if (!ownConversationId) return;

        // Strict filter: require the event to carry a conversationId that
        // matches this provider's conversation. surface_moved carries
        // fromConversationId/toConversationId instead of conversationId, so
        // accept the event when either matches.
        const d = data as {
          conversationId?: string;
          fromConversationId?: string;
          toConversationId?: string;
        };
        const matches =
          d.conversationId === ownConversationId ||
          d.fromConversationId === ownConversationId ||
          d.toConversationId === ownConversationId;
        if (!matches) return;

        if (event.eventType === 'agent.run.canvas_created') {
          dispatch({
            type: 'canvas_created',
            data: data as unknown as CanvasCreatedEventData,
          });
        } else if (event.eventType === 'agent.run.surface_update') {
          dispatchSurfaceUpdated({
            data: data as unknown as SurfaceUpdateEventData,
            timestamp: event.metadata.timestamp,
            scroll: autoScrollOnUpdate,
            pending: !autoScrollOnUpdate,
          });
        } else if (event.eventType === 'agent.run.surface_deleted') {
          const sd = data as unknown as SurfaceDeletedEventData;
          if (sd.success) {
            dispatch({ type: 'surface_removed', canvasId: sd.canvasId, surfaceId: sd.surfaceId });
          }
        } else if (event.eventType === 'agent.run.surface_moved') {
          const sm = data as unknown as SurfaceMovedEventData;
          if (!sm.success) return;
          dispatch({
            type: 'surface_moved',
            fromCanvasId: sm.fromCanvasId,
            toCanvasId: sm.toCanvasId,
            surfaceId: sm.surfaceId,
            version: sm.version,
            toConversationId: sm.toConversationId,
          });
        } else if (event.eventType === 'entity.canvas.created') {
          // A new canvas appeared (no live spec transport) — fetch its surfaces.
          loadCanvasContent((data as unknown as CanvasEntityUpdatedEventData).canvasId);
        } else if (event.eventType === 'entity.canvas.updated') {
          dispatch({
            type: 'canvas_metadata_updated',
            data: data as unknown as CanvasEntityUpdatedEventData,
          });
        } else if (event.eventType === 'entity.surface.created') {
          // A new surface appeared with no live spec transport — fetch the
          // canvas to render its content (otherwise it only shows on reload).
          loadCanvasContent((data as unknown as SurfaceEntityUpdatedEventData).canvasId);
        } else if (event.eventType === 'entity.surface.updated') {
          dispatch({
            type: 'surface_metadata_updated',
            data: data as unknown as SurfaceEntityUpdatedEventData,
          });
        }
      },
    );

    return unsubscribe;
  }, [subscribe, autoScrollOnUpdate, conversationId, dispatchSurfaceUpdated, loadCanvasContent]);

  // Show toast when a canvas gets a pending update (auto-scroll off only)
  const toast = useToast();
  toastRef.current = toast;
  const prevPendingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (autoScrollOnUpdate) return;
    const prev = prevPendingRef.current;
    for (const [canvasId, surfaceId] of state.pendingUpdates) {
      if (!prev.has(canvasId)) {
        const cv = state.canvases.get(canvasId);
        const title = cleanDisplayText(cv?.title) || 'Canvas';
        toast.info(`Surface updated in "${title}"`, {
          onClick: () => dispatch({ type: 'scroll_to_surface', canvasId, surfaceId }),
        });
      }
    }
    prevPendingRef.current = new Set(state.pendingUpdates.keys());
  }, [state.pendingUpdates, state.canvases, autoScrollOnUpdate, toast, dispatch]);

  // Expose dispatch for e2e testing and debugging in dev mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__canvasContext = {
        dispatch,
        getState: () => state,
      };
    }
  }, [dispatch, state]);

  return (
    <CanvasContext.Provider
      value={{
        state,
        dispatch,
        conversationId,
        spaceId,
        autoScrollOnUpdate,
        setSurfaceNodeUiState,
        setSurfaceUiStateRaw,
        flushSurfaceUiState,
      }}
    >
      {children}
    </CanvasContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useCanvases() {
  const ctx = useContext(CanvasContext);
  if (!ctx) {
    throw new Error('useCanvases must be used within a CanvasProvider');
  }

  const { state, dispatch, conversationId, spaceId, setSurfaceNodeUiState, setSurfaceUiStateRaw, flushSurfaceUiState } = ctx;
  const toast = useToast();
  const cvApi = spaceId ? spaceApi(spaceId).canvases : null;

  const setActiveCanvasId = useCallback(
    (canvasId: string) => {
      // Flush any pending uiState writes before unmounting the current
      // canvas's surfaces — otherwise rapid switching could lose edits.
      flushSurfaceUiState();
      dispatch({ type: 'set_active_canvas', canvasId });
    },
    [dispatch, flushSurfaceUiState],
  );

  const requestScrollToSurface = useCallback(
    (canvasId: string, surfaceId: string) => {
      dispatch({ type: 'scroll_to_surface', canvasId, surfaceId });
    },
    [dispatch],
  );

  const removeCanvas = useCallback(
    (canvasId: string) => {
      dispatch({ type: 'canvas_removed', canvasId });
      if (conversationId) {
        if (cvApi) cvApi.deleteCanvas(conversationId, canvasId).catch(() => {});
      }
    },
    [conversationId, dispatch],
  );

  const moveSurfaceToCanvas = useCallback(
    async (
      fromCanvasId: string,
      surfaceId: string,
      target:
        | { kind: 'existing'; canvasId: string }
        | { kind: 'new'; title: string; layout?: CanvasLayout },
    ) => {
      const fromKey = `${fromCanvasId}:${surfaceId}`;
      const surface = state.surfaces.get(fromKey);
      if (!surface) return;

      // Optimistic for existing target only — for "new" we don't know the
      // server-assigned canvasId yet.
      const isExisting = target.kind === 'existing';
      if (isExisting) {
        dispatch({
          type: 'surface_moved',
          fromCanvasId,
          toCanvasId: target.canvasId,
          surfaceId,
          version: surface.version + 1,
        });
        dispatch({ type: 'set_active_canvas', canvasId: target.canvasId });
      }

      try {
        const body =
          target.kind === 'existing'
            ? { surfaceIds: [surfaceId], targetCanvasId: target.canvasId }
            : {
                surfaceIds: [surfaceId],
                newCanvasTitle: target.title,
                newCanvasLayout: target.layout ?? 'stack',
              };
        if (!cvApi) throw new Error('spaceId required to move surfaces');
        const result = await cvApi.moveSurfaces(fromCanvasId, body);

        if (!isExisting) {
          // For new-canvas: dispatch now that we have the real toCanvasId.
          dispatch({
            type: 'surface_moved',
            fromCanvasId: result.fromCanvasId,
            toCanvasId: result.toCanvasId,
            surfaceId,
            version: result.surfaces[0]?.version ?? surface.version + 1,
            targetCanvas: result.canvasCreated
              ? {
                  title: result.toCanvasTitle ?? (target as { title: string }).title,
                  layout: result.toCanvasLayout ?? (target as { layout?: CanvasLayout }).layout ?? 'stack',
                }
              : undefined,
          });
          dispatch({ type: 'set_active_canvas', canvasId: result.toCanvasId });
        }

        const destTitle = cleanDisplayText(
          result.toCanvasTitle ?? state.canvases.get(result.toCanvasId)?.title,
        ) || 'canvas';
        toast.success(`Moved to "${destTitle}"`);
      } catch (err) {
        if (isExisting) {
          // Revert: re-insert the surface in the source canvas.
          dispatch({
            type: 'surface_updated',
            data: {
              runId: surface.runId ?? '',
              agentId: '',
              canvasId: fromCanvasId,
              surfaceId,
              title: surface.title,
              version: surface.version,
              spec: surface.spec,
              iteration: 0,
              createdAt: surface.createdAt,
              updatedAt: surface.updatedAt,
            },
          });
          // The optimistic dispatch left a stale entry in the target canvas.
          dispatch({ type: 'surface_removed', canvasId: target.canvasId, surfaceId });
          dispatch({ type: 'set_active_canvas', canvasId: fromCanvasId });
        }
        const msg = err instanceof Error ? err.message : 'Move failed';
        toast.error(`Move failed: ${msg}`);
      }
    },
    [dispatch, state.surfaces, state.canvases, toast],
  );

  const removeSurfaceNode = useCallback(
    (canvasId: string, surfaceId: string, nodeId: string) => {
      dispatch({ type: 'remove_surface_node', canvasId, surfaceId, nodeId });
    },
    [dispatch],
  );

  const reorderCanvases = useCallback(
    async (orderedNonDefaultIds: string[]) => {
      if (!conversationId) return;
      const snapshot = [...state.canvases.values()]
        .filter((w) => !w.isDefault)
        .sort(compareCanvases)
        .map((w) => w.canvasId);
      dispatch({ type: 'canvases_reordered', orderedCanvasIds: orderedNonDefaultIds });
      try {
        if (!cvApi) throw new Error('spaceId required to reorder canvases');
        await cvApi.reorderCanvases(conversationId, orderedNonDefaultIds);
      } catch (err) {
        dispatch({ type: 'canvases_reordered', orderedCanvasIds: snapshot });
        const msg = err instanceof Error ? err.message : 'Reorder failed';
        toast.error(`Reorder failed: ${msg}`);
      }
    },
    [conversationId, dispatch, state.canvases, toast],
  );

  const renameCanvas = useCallback(
    async (canvasId: string, title: string) => {
      if (!conversationId) return;
      const existing = state.canvases.get(canvasId);
      if (!existing) return;
      const previousTitle = existing.title;
      const trimmed = title.trim();
      if (trimmed === previousTitle) return;
      dispatch({
        type: 'canvas_metadata_updated',
        data: { canvasId, title: trimmed } as CanvasEntityUpdatedEventData,
      });
      try {
        if (!cvApi) throw new Error('spaceId required to update canvas');
        await cvApi.update(conversationId, canvasId, { title: trimmed });
      } catch (err) {
        dispatch({
          type: 'canvas_metadata_updated',
          data: { canvasId, title: previousTitle } as CanvasEntityUpdatedEventData,
        });
        const msg = err instanceof Error ? err.message : 'Rename failed';
        toast.error(`Rename failed: ${msg}`);
      }
    },
    [conversationId, dispatch, state.canvases, toast],
  );

  const reorderSurfaces = useCallback(
    async (canvasId: string, orderedSurfaceIds: string[]) => {
      const snapshot = [...state.surfaces.values()]
        .filter((s) => s.canvasId === canvasId)
        .sort(compareSurfaces)
        .map((s) => s.surfaceId);
      dispatch({ type: 'surfaces_reordered', canvasId, orderedSurfaceIds });
      try {
        if (!cvApi) throw new Error('spaceId required to reorder surfaces');
        await cvApi.reorderSurfaces(canvasId, orderedSurfaceIds);
      } catch (err) {
        dispatch({ type: 'surfaces_reordered', canvasId, orderedSurfaceIds: snapshot });
        const msg = err instanceof Error ? err.message : 'Reorder failed';
        toast.error(`Reorder failed: ${msg}`);
      }
    },
    [dispatch, state.surfaces, toast],
  );

  const moveSurface = useCallback(
    (canvasId: string, surfaceId: string, direction: 'up' | 'down') => {
      const ordered = [...state.surfaces.values()]
        .filter((s) => s.canvasId === canvasId)
        .sort(compareSurfaces);
      const idx = ordered.findIndex((s) => s.surfaceId === surfaceId);
      const next = direction === 'up' ? idx - 1 : idx + 1;
      if (idx === -1 || next < 0 || next >= ordered.length) return;
      const reordered = ordered.map((s) => s.surfaceId);
      [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
      void reorderSurfaces(canvasId, reordered);
    },
    [state.surfaces, reorderSurfaces],
  );

  // Read-only ref that mirrors loaded/loading sets so loadCanvasSurfaces can
  // dedupe without re-creating on every state change. The reducer also
  // no-ops `mark_canvas_loading` for already-loaded ids, but checking here
  // also skips the wasted `cvApi.get` request.
  const loadStateRef = useRef({
    loaded: state.loadedCanvases,
    loading: state.loadingCanvases,
  });
  loadStateRef.current = {
    loaded: state.loadedCanvases,
    loading: state.loadingCanvases,
  };

  const loadCanvasSurfaces = useCallback(async (canvasId: string) => {
    if (!conversationId) return;
    if (
      loadStateRef.current.loaded.has(canvasId) ||
      loadStateRef.current.loading.has(canvasId)
    ) {
      return;
    }
    // Mark loading so the CanvasPanel can show a spinner and the lazy-load
    // useEffect in CanvasProvider skips this id while we're in flight.
    dispatch({ type: 'mark_canvas_loading', canvasId });
    try {
      if (!cvApi) return null;
      const result = await cvApi.get(conversationId, canvasId).catch(() => null);
      if (result) {
        for (const s of result.surfaces) {
          dispatch({ type: 'surface_updated', data: { ...s, runId: '', agentId: '', iteration: 0 } });
        }
      }
    } finally {
      dispatch({ type: 'mark_canvas_loaded', canvasId });
    }
  }, [conversationId, dispatch]);

  const isActiveCanvasLoading =
    !!state.activeCanvasId && state.loadingCanvases.has(state.activeCanvasId);

  return {
    canvases: state.canvases,
    surfaces: state.surfaces,
    activeCanvasId: state.activeCanvasId,
    loaded: state.loaded,
    hasCanvases: state.canvases.size > 0,
    scrollToSurface: state.scrollToSurface,
    pendingUpdates: state.pendingUpdates,
    loadingCanvases: state.loadingCanvases,
    isActiveCanvasLoading,
    conversationId,
    spaceId,
    setActiveCanvasId,
    removeCanvas,
    removeSurfaceNode,
    requestScrollToSurface,
    loadCanvasSurfaces,
    moveSurfaceToCanvas,
    reorderCanvases,
    renameCanvas,
    reorderSurfaces,
    moveSurface,
    setSurfaceNodeUiState,
    setSurfaceUiStateRaw,
    flushSurfaceUiState,
    dispatch,
  };
}
